import { isEmpty } from 'lodash';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { removeEmptyValuesFromObject } from '../../../../../../utils/removeEmptyValuesFromObject.ts';
import { ChatEnum, EventType } from '../../../../Chat.enum.ts';
import { updateEventAttachmentParser } from '../../../../Chat.functions.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatService } from '../../../../Chat.service.ts';
import {
  tAttachment,
  tChatEvent,
  tUpdateEventData,
} from '../../../../Chat.types.ts';
import useMessageBubble from '../../../MessageContainer/components/EventBubble/components/MessageBubble/MessageBubble.hook.tsx';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { tNotesActionHook } from './NotesAction.types.ts';
export default function useNotesAction(): tNotesActionHook {
  const [loading, setLoading] = useState<boolean>(false);

  const {
    selectedChat,
    setSelectedChat,
    chatEvents,
    setChatEvents,
    notes,
    setNotes,
  } = useChatContext();
  const { addAlert } = useToastContext();
  const { currentUserId } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { replaceMessageWithVariable } = useMessageBubble();
  const {
    messageBarValue,
    setMessageBarValue,
    setSelectedAction,
    setChatAttachments,
  } = useMessageBarContext();

  async function createNewNote(
    title: string,
    attachments?: tAttachment[],
    customText?: string,
  ) {
    try {
      if (!selectedChat?.id || !currentUserId || !chatEvents) {
        return;
      }

      setLoading(true);

      const noteId = uuid();

      // Use customText if provided, otherwise use messageBarValue
      const noteText = customText !== undefined ? customText : messageBarValue;

      await ChatService.createNote({
        chatId: selectedChat.id,
        data: {
          id: noteId,
          content: {
            text: replaceMessageWithVariable(noteText) || '',
            attachments:
              attachments?.map((attachment) =>
                updateEventAttachmentParser(attachment),
              ) || [],
          },
          created_by: currentUserId,
          title: title,
          note: {
            title: title,
            type: 0,
          },
        },
      });

      // TODO: check what happened to the notes sockets
      const newNote: tChatEvent = {
        agendaEvent: null,
        chatID: selectedChat.id,
        content: {
          attachments: attachments || [],
          linkPreview: null,
          quotedEventID: null,
          text: replaceMessageWithVariable(noteText) || '',
        },
        createdAt: new Date().toISOString(),
        createdBy: currentUserId,
        deletedAt: '',
        followUp: null,
        id: noteId,
        message: null,
        note: {
          title: title,
        },
        type: EventType.NOTE,
        updatedAt: new Date().toISOString(),
      };

      setNotes([newNote, ...notes]);

      setChatEvents([...chatEvents, newNote]);

      setMessageBarValue('');
      setSelectedAction(ChatEnum.CHAT);
      setChatAttachments([]);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos crear la nota ${error}`,
      });
    }
    setLoading(false);
  }

  async function deleteEventNote(eventId: string) {
    try {
      if (
        !currentWorkspaceId ||
        !currentUserId ||
        isEmpty(notes) ||
        !chatEvents
      ) {
        return;
      }

      setLoading(true);

      await ChatService.deleteEvent(
        {
          eventId: eventId,
          workspaceId: currentWorkspaceId,
        },
        currentUserId,
      );

      const newEvents = selectedChat?.chatEvents.events.filter(
        (event) => event.id !== eventId,
      );

      const newNotes = notes.filter((note) => note.id !== eventId);

      if (!selectedChat || !newEvents) {
        return;
      }

      setNotes(newNotes);
      setSelectedChat({
        ...selectedChat,
        chatEvents: {
          ...selectedChat.chatEvents,
          events: newEvents,
        },
      });
      setChatEvents(chatEvents?.filter((event) => event.id !== eventId));
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Nota eliminada correctamente',
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos eliminar la nota ${error}`,
      });
    }
    setLoading(false);
  }

  async function updateEvent(eventId: string, data: tUpdateEventData) {
    try {
      if (!currentWorkspaceId || !currentUserId || !chatEvents) {
        return;
      }

      await ChatService.updateEvent(
        {
          eventId: eventId,
          workspaceId: currentWorkspaceId,
          data: {
            ...data,
            content: {
              ...data.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
            },
          },
        },
        currentUserId,
      );

      if (!selectedChat || isEmpty(notes)) {
        return;
      }

      const updatedNotes: tChatEvent[] = notes.map((note) => {
        if (note.id === eventId) {
          return {
            ...note,
            note: {
              title: data?.note?.title || '',
            },
            content: {
              ...note.content,
              text: replaceMessageWithVariable(data?.content?.text) || '',
              attachments:
                data.content?.attachments?.map(
                  (attachment) =>
                    removeEmptyValuesFromObject(attachment) as tAttachment,
                ) || [],
              linkPreview: note.content?.linkPreview || null,
              quotedEventID: note.content?.quotedEventID || null,
            },
          };
        }
        return note;
      });

      const updatedEvents: tChatEvent[] = chatEvents.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            note: {
              title: data?.note?.title || '',
            },
            content: {
              ...event.content,
              text: replaceMessageWithVariable(data?.content?.text) || '',
              attachments:
                data.content?.attachments?.map(
                  (file) =>
                    ({
                      vcard: null,
                      decryption: null,
                      duration: null,
                      filename: file.filename,
                      latitude: null,
                      longitude: null,
                      mediaType: file.media_type,
                      mimeType: file.mimetype,
                      url: file.url,
                    }) as tAttachment,
                ) || [],
              linkPreview: event.content?.linkPreview || null,
              quotedEventID: event.content?.quotedEventID || null,
            },
          };
        }
        return event;
      });

      setNotes(updatedNotes);
      setSelectedChat({
        ...selectedChat,
        chatEvents: {
          ...selectedChat.chatEvents,
          events: updatedEvents,
        },
      });

      setChatEvents(updatedEvents);

      setSelectedAction(ChatEnum.CHAT);
      setMessageBarValue('');
      setChatAttachments([]);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos actualizar la nota ${error}`,
      });
    }
  }

  return {
    createNewNote,
    loading,
    setLoading,
    deleteEventNote,
    updateEvent,
  };
}
